import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, X } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { SEO } from "@/components/motion";
import { DiscountCodes } from "@/components/admin/DiscountCodes";
import { RequestActions, STATUS_LABELS } from "@/components/admin/RequestActions";

const TABS = ["Overview", "Bookings", "Enquiries", "Services", "Extras", "Discount Codes", "Availability", "Settings"];
const BOOKING_STATUSES = Object.keys(STATUS_LABELS);
const gbp = (v) => `£${Number(v || 0).toLocaleString("en-GB", { maximumFractionDigits: 2 })}`;
const extraNames = (b) => (b.extras || []).map((x) => (typeof x === "string" ? x : `${x.name} (${gbp(x.price)})`));
const ENQUIRY_STATUSES = ["new", "replied", "closed"];

const inputCls =
  "w-full rounded-lg border border-subtle bg-surface px-3 py-2.5 text-sm text-ink outline-none focus:border-blue";

function Badge({ status }) {
  const colors = {
    new: "bg-blue/10 text-blue",
    confirmed: "bg-sage-light text-sage-dark",
    completed: "bg-sage/20 text-sage-dark",
    cancelled: "bg-red-100 text-red-700",
    replied: "bg-sage-light text-sage-dark",
    closed: "bg-subtle text-faint",
    awaiting_payment: "bg-subtle text-faint",
    paid: "bg-sage-light text-sage-dark",
    pending: "bg-subtle text-faint",
    failed: "bg-red-100 text-red-700",
    expired: "bg-subtle text-faint",
    request_received: "bg-blue/10 text-blue",
    under_review: "bg-blue/10 text-blue",
    awaiting_customer_response: "bg-sand text-body",
    confirmed_paid: "bg-sage-light text-sage-dark",
    declined: "bg-red-100 text-red-700",
    payment_failed_expired: "bg-red-100 text-red-700",
    not_requested: "bg-subtle text-faint",
    refunded: "bg-blue/10 text-blue",
    partially_refunded: "bg-blue/10 text-blue",
  };
  return (
    <span className={`rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-[0.12em] ${colors[status] || "bg-subtle text-faint"}`}>
      {STATUS_LABELS[status] || String(status).replace(/_/g, " ")}
    </span>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [auth, setAuth] = useState("loading");
  const [tab, setTab] = useState("Overview");
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [services, setServices] = useState([]);
  const [extras, setExtras] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [settings, setSettings] = useState(null);
  const [selected, setSelected] = useState(null); // booking or enquiry detail
  const [bookingFilter, setBookingFilter] = useState("");
  const [newBlock, setNewBlock] = useState({ date: "", note: "" });
  const [notice, setNotice] = useState("");
  const [templateText, setTemplateText] = useState(null);

  const flash = (msg) => {
    setNotice(msg);
    setTimeout(() => setNotice(""), 2500);
  };

  const loadAll = async () => {
    try {
      const [st, b, en, sv, ex, av, se] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/bookings"),
        api.get("/admin/enquiries"),
        api.get("/admin/services"),
        api.get("/extras"),
        api.get("/admin/availability"),
        api.get("/admin/settings"),
      ]);
      setStats(st.data);
      setBookings(b.data);
      setEnquiries(en.data);
      setServices(sv.data);
      setExtras(ex.data);
      setAvailability(av.data);
      setSettings(se.data);
    } catch (e) {
      /* handled by interceptor / auth guard */
    }
  };

  useEffect(() => {
    api.get("/auth/me")
      .then(() => {
        setAuth("in");
        loadAll();
      })
      .catch(() => navigate("/admin/login"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    navigate("/admin/login");
  };

  const patchBooking = async (id, patch) => {
    const { data } = await api.patch(`/admin/bookings/${id}`, patch);
    setBookings((bs) => bs.map((b) => (b.id === id ? data : b)));
    setSelected({ ...data, _type: "booking" });
    flash("Booking updated");
  };

  const refundBooking = async (b) => {
    if (!window.confirm(`Refund ${gbp(b.total_price)} to ${b.name} for booking ${b.booking_ref}?\n\nThe full amount goes back to the customer's card through Stripe and the booking is marked cancelled. This cannot be undone.`)) return;
    try {
      const { data } = await api.post(`/admin/bookings/${b.id}/refund`);
      setBookings((bs) => bs.map((x) => (x.id === b.id ? data : x)));
      setSelected({ ...data, _type: "booking" });
      flash("Refund issued");
    } catch (err) {
      window.alert(apiError(err));
    }
  };

  const patchEnquiry = async (id, patch) => {
    const { data } = await api.patch(`/admin/enquiries/${id}`, patch);
    setEnquiries((es) => es.map((e) => (e.id === id ? data : e)));
    setSelected(data);
    flash("Enquiry updated");
  };

  const saveService = async (svc) => {
    const { data } = await api.put(`/admin/services/${svc.slug}`, {
      name: svc.name, tagline: svc.tagline, short_desc: svc.short_desc, long_desc: svc.long_desc,
      image: svc.image, base_price: svc.base_price === "" || svc.base_price == null ? null : Number(svc.base_price),
      price_note: svc.price_note, active: svc.active,
    });
    setServices((ss) => ss.map((s) => (s.slug === svc.slug ? data : s)));
    flash("Service saved");
  };

  const addBlock = async () => {
    if (!newBlock.date) return;
    await api.post("/admin/availability", newBlock);
    const { data } = await api.get("/admin/availability");
    setAvailability(data);
    setNewBlock({ date: "", note: "" });
    flash("Date blocked");
  };

  const removeBlock = async (id) => {
    await api.delete(`/admin/availability/${id}`);
    setAvailability((a) => a.filter((x) => x.id !== id));
    flash("Date unblocked");
  };

  const saveSettings = async () => {
    const { data } = await api.put("/admin/settings", {
      pricing_enabled: settings.pricing_enabled, payments_enabled: settings.payments_enabled,
      announcement: settings.announcement,
    });
    setSettings(data);
    flash("Settings saved");
  };

  const buildQuoteTemplate = (b) => {
    const first = (b.name || "").split(" ")[0] || "there";
    const property = [b.property_type, b.bedrooms && `${b.bedrooms} bed`, b.bathrooms && `${b.bathrooms} bath`, b.property_size]
      .filter(Boolean).join(", ");
    const extrasLines = b.extras?.length ? extraNames(b).map((x) => `- ${x}`).join("\n") + "\n" : "";
    return `Dear ${first},

Thank you for your booking request with HOMELARA (reference ${b.booking_ref}).

Your clean:
- Service: ${b.service_name}
- Date: ${b.date}${b.time_slot_label ? ` (${b.time_slot_label})` : ""}
- Property: ${property}
- Extras: ${b.extras?.length ? extraNames(b).join(", ") : "None"}

Your booking:
- ${b.service_name}: ${b.base_price != null ? gbp(b.base_price) : "£"}
${extrasLines}- Total: ${b.total_price != null ? gbp(b.total_price) : "£"}

To confirm your booking, simply reply to this email and I will finalise everything for you.

Warm regards,
Laura
HOMELARA
lauraflorez@homelara.uk
WhatsApp: +44 7939 365873`;
  };

  if (auth === "loading") {
    return <main className="flex min-h-screen items-center justify-center bg-canvas"><p className="font-mono text-xs uppercase tracking-[0.2em] text-faint">Loading admin...</p></main>;
  }

  const filteredBookings = bookingFilter ? bookings.filter((b) => b.status === bookingFilter) : bookings;

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-canvas">
      <SEO title="Admin | HOMELARA" description="HOMELARA private administration dashboard." />

      <header className="sticky top-0 z-40 border-b border-subtle bg-canvas/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 sm:px-8 py-4">
          <div className="flex items-baseline gap-3">
            <span className="font-serif text-2xl font-semibold tracking-[0.12em] text-blue">HOMELARA</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-sage-dark">Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="admin-view-site" className="hidden sm:block text-xs text-faint hover:text-blue transition-colors">View site</Link>
            <button onClick={logout} data-testid="admin-logout-button" className="inline-flex items-center gap-2 rounded-full border border-subtle px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-ink hover:border-blue hover:text-blue transition-colors">
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
        <div className="mx-auto max-w-7xl overflow-x-auto px-5 sm:px-8">
          <nav className="flex gap-1 pb-2" data-testid="admin-tabs">
            {TABS.map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                data-testid={`admin-tab-${t.toLowerCase().replace(/\s+/g, "-")}`}
                className={`whitespace-nowrap rounded-full px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] transition-colors ${
                  tab === t ? "bg-blue text-canvas" : "text-faint hover:text-blue"
                }`}
              >
                {t}
              </button>
            ))}
          </nav>
        </div>
      </header>

      {notice && (
        <div data-testid="admin-notice" className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-full bg-blue px-6 py-3 font-mono text-[11px] uppercase tracking-[0.15em] text-canvas shadow-2xl">
          {notice}
        </div>
      )}

      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-10">
        {tab === "Overview" && stats && (
          <div data-testid="admin-overview">
            <h1 className="font-serif text-3xl tracking-tight text-ink">Overview</h1>
            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {[
                ["Total bookings", stats.bookings_total],
                ["Paid bookings", stats.bookings_paid],
                ["New requests to review", stats.bookings_new],
                ["Awaiting customer", stats.bookings_awaiting_customer],
                ["Awaiting payment", stats.bookings_awaiting_payment],
                ["Confirmed — Paid", stats.bookings_confirmed],
                ["Declined / cancelled", stats.bookings_cancelled],
                ["New enquiries", stats.enquiries_new],
                ["Total enquiries", stats.enquiries_total],
                ["Blocked dates", stats.blocked_dates],
              ].map(([label, val]) => (
                <div key={label} className="rounded-2xl border border-subtle bg-surface p-6">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-faint">{label}</p>
                  <p className="mt-2 font-serif text-4xl text-blue">{val}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "Bookings" && (
          <div data-testid="admin-bookings">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <h1 className="font-serif text-3xl tracking-tight text-ink">Booking requests</h1>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => setBookingFilter("")} data-testid="bookings-filter-all" className={`rounded-full px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.12em] ${!bookingFilter ? "bg-blue text-canvas" : "text-faint"}`}>All</button>
                {BOOKING_STATUSES.map((s) => (
                  <button key={s} onClick={() => setBookingFilter(s)} data-testid={`bookings-filter-${s}`} className={`rounded-full px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.12em] ${bookingFilter === s ? "bg-blue text-canvas" : "text-faint"}`}>
                    {STATUS_LABELS[s]}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-6 overflow-hidden rounded-2xl border border-subtle bg-surface">
              {filteredBookings.length === 0 && <p className="p-8 text-sm text-faint">No booking requests yet.</p>}
              {filteredBookings.map((b) => (
                <button
                  key={b.id}
                  onClick={() => { setSelected({ ...b, _type: "booking" }); setTemplateText(null); }}
                  data-testid={`booking-row-${b.booking_ref}`}
                  className="grid w-full grid-cols-2 sm:grid-cols-[110px_1fr_150px_70px_190px] items-center gap-2 border-b border-subtle px-5 py-4 text-left transition-colors last:border-0 hover:bg-warmcard"
                >
                  <span className="font-mono text-xs text-blue">{b.booking_ref}</span>
                  <span className="text-sm text-ink">{b.name} <span className="text-faint">&middot; {b.service_name}</span></span>
                  <span className="text-xs text-faint">{b.date} &middot; {b.time_slot_label}</span>
                  <span className="text-sm text-ink">{b.total_price != null ? gbp(b.total_price) : "—"}</span>
                  <span className="flex gap-1.5 justify-self-end">{b.payment_status && <Badge status={b.payment_status} />}<Badge status={b.status} /></span>
                </button>
              ))}
            </div>
          </div>
        )}

        {tab === "Enquiries" && (
          <div data-testid="admin-enquiries">
            <h1 className="font-serif text-3xl tracking-tight text-ink">Enquiries</h1>
            <div className="mt-6 overflow-hidden rounded-2xl border border-subtle bg-surface">
              {enquiries.length === 0 && <p className="p-8 text-sm text-faint">No enquiries yet.</p>}
              {enquiries.map((e) => (
                <button
                  key={e.id}
                  onClick={() => { setSelected({ ...e, _type: "enquiry" }); setTemplateText(null); }}
                  data-testid={`enquiry-row-${e.id.slice(0, 8)}`}
                  className="grid w-full grid-cols-2 sm:grid-cols-[1fr_160px_110px] items-center gap-2 border-b border-subtle px-5 py-4 text-left transition-colors last:border-0 hover:bg-warmcard"
                >
                  <span className="text-sm text-ink">{e.name} <span className="text-faint">&middot; {e.service || "General"}</span></span>
                  <span className="text-xs text-faint">{new Date(e.created_at).toLocaleDateString("en-GB")}</span>
                  <span className="justify-self-end"><Badge status={e.status} /></span>
                </button>
              ))}
            </div>
          </div>
        )}

        {tab === "Services" && (
          <div data-testid="admin-services" className="space-y-6">
            <h1 className="font-serif text-3xl tracking-tight text-ink">Services</h1>
            <p className="text-sm text-faint">Edit names, descriptions, images and (later) prices. Leave the price empty to keep the service quote-based.</p>
            {services.map((s) => (
              <div key={s.slug} className="rounded-2xl border border-subtle bg-surface p-6" data-testid={`admin-service-${s.slug}`}>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.15em] text-faint">Name</label>
                    <input className={inputCls} value={s.name} onChange={(e) => setServices(services.map((x) => x.slug === s.slug ? { ...x, name: e.target.value } : x))} />
                  </div>
                  <div>
                    <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.15em] text-faint">Tagline</label>
                    <input className={inputCls} value={s.tagline} onChange={(e) => setServices(services.map((x) => x.slug === s.slug ? { ...x, tagline: e.target.value } : x))} />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.15em] text-faint">Short description (cards)</label>
                    <textarea rows={2} className={inputCls} value={s.short_desc} onChange={(e) => setServices(services.map((x) => x.slug === s.slug ? { ...x, short_desc: e.target.value } : x))} />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.15em] text-faint">Long description (detail page intro)</label>
                    <textarea rows={3} className={inputCls} value={s.long_desc} onChange={(e) => setServices(services.map((x) => x.slug === s.slug ? { ...x, long_desc: e.target.value } : x))} />
                  </div>
                  <div>
                    <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.15em] text-faint">Image URL</label>
                    <input className={inputCls} value={s.image} onChange={(e) => setServices(services.map((x) => x.slug === s.slug ? { ...x, image: e.target.value } : x))} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.15em] text-faint">Base price £ (empty = quote)</label>
                      <input className={inputCls} value={s.base_price ?? ""} placeholder="—" onChange={(e) => setServices(services.map((x) => x.slug === s.slug ? { ...x, base_price: e.target.value } : x))} />
                    </div>
                    <div>
                      <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.15em] text-faint">Visible on site</label>
                      <select className={inputCls} value={String(s.active)} onChange={(e) => setServices(services.map((x) => x.slug === s.slug ? { ...x, active: e.target.value === "true" } : x))}>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </select>
                    </div>
                  </div>
                </div>
                <button onClick={() => saveService(s)} data-testid={`admin-service-save-${s.slug}`} className="mt-5 rounded-full bg-blue px-6 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] text-canvas hover:bg-blue-hover transition-colors">
                  Save {s.name}
                </button>
              </div>
            ))}
          </div>
        )}

        {tab === "Extras" && (
          <div data-testid="admin-extras">
            <h1 className="font-serif text-3xl tracking-tight text-ink">Optional extras</h1>
            <p className="mt-2 text-sm text-faint">Approved V1 prices. These are fixed in the booking system and applied automatically at checkout.</p>
            <div className="mt-6 overflow-hidden rounded-2xl border border-subtle bg-surface">
              {extras.map((ex) => (
                <div key={ex.id} className="flex items-center justify-between border-b border-subtle px-5 py-4 last:border-0" data-testid={`admin-extra-${ex.id}`}>
                  <span className="text-sm text-ink">{ex.name} <span className="text-faint">&middot; {ex.description}</span></span>
                  <span className="font-serif text-lg text-blue">{gbp(ex.price)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "Discount Codes" && <DiscountCodes flash={flash} />}

        {tab === "Availability" && (
          <div data-testid="admin-availability">
            <h1 className="font-serif text-3xl tracking-tight text-ink">Availability</h1>
            <p className="mt-2 text-sm text-faint">Block dates when HOMELARA is fully booked. Customers can't select blocked dates in the booking flow.</p>
            <div className="mt-6 grid gap-3 rounded-2xl border border-dashed border-sage bg-sage-light/40 p-5 sm:grid-cols-[180px_1fr_auto]">
              <input type="date" className={inputCls} value={newBlock.date} onChange={(e) => setNewBlock({ ...newBlock, date: e.target.value })} data-testid="admin-block-date" />
              <input className={inputCls} placeholder="Note (optional, internal)" value={newBlock.note} onChange={(e) => setNewBlock({ ...newBlock, note: e.target.value })} />
              <button onClick={addBlock} data-testid="admin-block-add" className="rounded-full bg-blue px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.12em] text-canvas">Block date</button>
            </div>
            <div className="mt-6 space-y-2">
              {availability.length === 0 && <p className="text-sm text-faint">No blocked dates.</p>}
              {availability.map((a) => (
                <div key={a.id} className="flex items-center justify-between rounded-xl border border-subtle bg-surface px-5 py-3">
                  <span className="text-sm text-ink">{a.date} {a.note && <span className="text-faint">&middot; {a.note}</span>}</span>
                  <button onClick={() => removeBlock(a.id)} className="text-xs text-red-700 hover:underline">Unblock</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "Settings" && settings && (
          <div data-testid="admin-settings" className="max-w-2xl">
            <h1 className="font-serif text-3xl tracking-tight text-ink">Settings</h1>
            <div className="mt-6 space-y-4 rounded-2xl border border-subtle bg-surface p-6">
              <label className="flex items-center justify-between gap-4">
                <span className="text-sm text-ink">Show prices on the website</span>
                <input type="checkbox" checked={!!settings.pricing_enabled} onChange={(e) => setSettings({ ...settings, pricing_enabled: e.target.checked })} data-testid="settings-pricing-toggle" className="h-5 w-5 accent-[#0F2942]" />
              </label>
              <label className="flex items-center justify-between gap-4">
                <span className="text-sm text-ink">Online payments active (Stripe)</span>
                <input type="checkbox" checked={!!settings.payments_enabled} onChange={(e) => setSettings({ ...settings, payments_enabled: e.target.checked })} data-testid="settings-payments-toggle" className="h-5 w-5 accent-[#0F2942]" />
              </label>
              <button onClick={saveSettings} data-testid="settings-save" className="rounded-full bg-blue px-6 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] text-canvas hover:bg-blue-hover transition-colors">
                Save settings
              </button>
            </div>
            <div className="mt-6 rounded-2xl border border-sage/40 bg-sage-light/40 p-6">
              <h2 className="font-sans font-semibold text-blue">Stripe &amp; online payments</h2>
              <p className="mt-2 text-sm leading-relaxed text-body">
                Customers pay the fixed V1 price plus selected extras through Stripe Checkout (one-time payments in GBP).
                A booking is marked paid only after Stripe confirms the payment.
              </p>
              <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.15em] text-faint" data-testid="stripe-status">
                Status: Connected - {settings.stripe_mode === "live" ? "LIVE" : "TEST"} mode
              </p>
            </div>
            <div className="mt-6 rounded-2xl border border-subtle bg-surface p-6 text-sm text-body">
              <h2 className="font-sans font-semibold text-blue">Admin access</h2>
              <p className="mt-2">Admin email and password are managed securely in the server environment configuration. Contact your developer to change them.</p>
            </div>
          </div>
        )}
      </div>

      {/* Detail modal */}
      {selected && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-blue-midnight/60 p-0 sm:p-6" onClick={() => setSelected(null)} data-testid="admin-detail-modal">
          <div className="max-h-[88vh] w-full max-w-2xl overflow-y-auto rounded-t-3xl sm:rounded-3xl bg-surface p-7 sm:p-9" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-sage-dark">
                  {selected._type === "booking" ? `Booking ${selected.booking_ref}` : "Enquiry"}
                </p>
                <h2 className="mt-1 font-serif text-3xl tracking-tight text-ink">{selected.name}</h2>
              </div>
              <button onClick={() => setSelected(null)} data-testid="admin-detail-close" className="rounded-full border border-subtle p-2 text-faint hover:text-ink"><X size={16} /></button>
            </div>

            <dl className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
              {selected._type === "booking" ? (
                <>
                  <div><dt className="text-faint">Service</dt><dd className="text-ink">{selected.service_name}</dd></div>
                  <div><dt className="text-faint">Status</dt><dd><Badge status={selected.status} /></dd></div>
                  <div><dt className="text-faint">Date</dt><dd className="text-ink">{selected.date} &middot; {selected.time_slot_label}</dd></div>
                  <div><dt className="text-faint">Property</dt><dd className="text-ink">{selected.property_label || selected.property_type} &middot; Bedrooms: {selected.bedrooms || "—"} &middot; Bathrooms: {selected.bathrooms || "—"}</dd></div>
                  {selected.frequency_label && <div><dt className="text-faint">Frequency</dt><dd className="text-ink">{selected.frequency_label}</dd></div>}
                  <div className="sm:col-span-2"><dt className="text-faint">Extras</dt><dd className="text-ink">{selected.extras?.length ? extraNames(selected).join(", ") : "None"}</dd></div>
                  <div><dt className="text-faint">Total price</dt><dd className="text-ink" data-testid="admin-detail-total">{selected.total_price != null ? gbp(selected.total_price) : "—"}</dd></div>
                  <div className="sm:col-span-2" data-testid="admin-detail-discount">
                    <dt className="text-faint">Discount</dt>
                    <dd className="text-ink">
                      {selected.promo_code
                        ? `${selected.promo_code} (${selected.discount_type === "percent" ? `${selected.discount_value}%` : gbp(selected.discount_value)} off cleaning) · Cleaning ${gbp(selected.original_cleaning_price)} − ${gbp(selected.discount_amount)} = ${gbp(selected.final_cleaning_price)} · Extras ${gbp(selected.extras_total)} · Total ${gbp(selected.total_price)}`
                        : "No promo code - normal price"}
                    </dd>
                  </div>
                  <div><dt className="text-faint">Payment</dt><dd><Badge status={selected.payment_status || "pending"} /></dd></div>
                  {selected.refund_amount != null && (
                    <div data-testid="admin-detail-refund"><dt className="text-faint">Refunded</dt><dd className="text-ink">{gbp(selected.refund_amount)}{selected.refunded_at && ` · ${new Date(selected.refunded_at).toLocaleString("en-GB")}`}</dd></div>
                  )}
                  {selected.payment_status === "paid" && selected.stripe_payment_intent_id && (
                    <div className="sm:col-span-2">
                      <button onClick={() => refundBooking(selected)} data-testid="admin-refund-button"
                        className="rounded-full border border-red-300 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] text-red-700 transition-colors hover:bg-red-50">
                        Refund {gbp(selected.total_price)}
                      </button>
                    </div>
                  )}
                  <div className="sm:col-span-2"><dt className="text-faint">Stripe reference</dt><dd className="font-mono text-xs text-ink break-all">{selected.stripe_payment_intent_id || selected.stripe_session_id || "—"}</dd></div>
                  <div><dt className="text-faint">Created</dt><dd className="text-ink">{selected.created_at ? new Date(selected.created_at).toLocaleString("en-GB") : "—"}</dd></div>
                  {selected.parking_review && <div><dt className="text-faint">Parking</dt><dd className="text-ink">Customer flagged parking / congestion / tolls</dd></div>}
                  {selected.access_instructions && <div className="sm:col-span-2"><dt className="text-faint">Access instructions</dt><dd className="text-ink">{selected.access_instructions}</dd></div>}
                  <div><dt className="text-faint">Email</dt><dd className="text-ink">{selected.email}</dd></div>
                  <div><dt className="text-faint">Phone</dt><dd className="text-ink">{selected.phone}</dd></div>
                  <div className="sm:col-span-2"><dt className="text-faint">Address</dt><dd className="text-ink">{selected.address}{selected.postcode && `, ${selected.postcode}`}</dd></div>
                  {selected.notes && <div className="sm:col-span-2"><dt className="text-faint">Customer notes</dt><dd className="text-ink">{selected.notes}</dd></div>}
                </>
              ) : (
                <>
                  <div><dt className="text-faint">Email</dt><dd className="text-ink">{selected.email}</dd></div>
                  <div><dt className="text-faint">Phone</dt><dd className="text-ink">{selected.phone || "—"}</dd></div>
                  <div><dt className="text-faint">Service</dt><dd className="text-ink">{selected.service || "—"}</dd></div>
                  <div><dt className="text-faint">Property type</dt><dd className="text-ink">{selected.property_type || "—"}</dd></div>
                  <div><dt className="text-faint">Preferred date</dt><dd className="text-ink">{selected.preferred_date || "—"}</dd></div>
                  <div><dt className="text-faint">Status</dt><dd><Badge status={selected.status} /></dd></div>
                  <div className="sm:col-span-2"><dt className="text-faint">Message</dt><dd className="text-ink whitespace-pre-wrap">{selected.message}</dd></div>
                </>
              )}
            </dl>

            {selected._type === "booking" && (
              <RequestActions booking={selected} flash={flash} onUpdated={(data) => {
                setBookings((bs) => bs.map((x) => (x.id === data.id ? data : x)));
                setSelected({ ...data, _type: "booking" });
              }} />
            )}

            <div className="mt-7 grid gap-4 sm:grid-cols-2">
              {selected._type !== "booking" && (
              <div>
                <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.15em] text-faint">Status</label>
                <select
                  className={inputCls}
                  value={selected.status}
                  data-testid="admin-detail-status"
                  onChange={(e) =>
                    selected._type === "booking"
                      ? patchBooking(selected.id, { status: e.target.value })
                      : patchEnquiry(selected.id, { status: e.target.value })
                  }
                >
                  {ENQUIRY_STATUSES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              )}
              <div className="sm:col-span-2">
                <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.15em] text-faint">Internal notes (only you see these)</label>
                <textarea
                  rows={3}
                  className={inputCls}
                  defaultValue={selected.internal_notes || ""}
                  data-testid="admin-detail-notes"
                  onBlur={(e) =>
                    selected._type === "booking"
                      ? patchBooking(selected.id, { internal_notes: e.target.value })
                      : patchEnquiry(selected.id, { internal_notes: e.target.value })
                  }
                />
              </div>
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href={`mailto:${selected.email}`} data-testid="admin-detail-reply" className="inline-flex items-center gap-2 rounded-full bg-blue px-6 py-3 font-mono text-[10px] uppercase tracking-[0.15em] text-canvas hover:bg-blue-hover transition-colors">
                Reply by email
              </a>
              {selected._type === "booking" && (
                <button
                  onClick={() => setTemplateText(templateText === null ? buildQuoteTemplate(selected) : null)}
                  data-testid="admin-quote-template-toggle"
                  className="inline-flex items-center gap-2 rounded-full border border-blue px-6 py-3 font-mono text-[10px] uppercase tracking-[0.15em] text-blue hover:bg-blue hover:text-canvas transition-colors"
                >
                  {templateText === null ? "Quote reply template" : "Hide template"}
                </button>
              )}
            </div>

            {selected._type === "booking" && templateText !== null && (
              <div className="mt-5 rounded-2xl border border-sage/40 bg-sage-light/40 p-5" data-testid="admin-quote-template">
                <p className="text-xs text-faint leading-relaxed">
                  Ready-made reply with the booking details pre-filled. Type your prices where you see the £ signs,
                  edit anything you like, then copy it or open it straight in your email app.
                </p>
                <textarea
                  rows={16}
                  className={`${inputCls} mt-3 font-mono text-xs leading-relaxed`}
                  value={templateText}
                  onChange={(e) => setTemplateText(e.target.value)}
                  data-testid="admin-quote-template-text"
                />
                <div className="mt-3 flex flex-wrap gap-3">
                  <button
                    onClick={() => { navigator.clipboard?.writeText(templateText); flash("Template copied"); }}
                    data-testid="admin-quote-template-copy"
                    className="rounded-full bg-blue px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.12em] text-canvas hover:bg-blue-hover transition-colors"
                  >
                    Copy template
                  </button>
                  <a
                    href={`mailto:${selected.email}?subject=${encodeURIComponent(`Your HOMELARA quote - ${selected.booking_ref}`)}&body=${encodeURIComponent(templateText)}`}
                    data-testid="admin-quote-template-mailto"
                    className="rounded-full border border-subtle px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.12em] text-ink hover:border-blue hover:text-blue transition-colors"
                  >
                    Open in email app
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </main>
  );
}
